import { useEffect, useMemo, useRef, useState } from 'react';
import { activePropertySelector } from '../../store/selectors/session-selectors';
import { useAppSelector } from '../../store/store';
import { integrationsConfig } from '../../config/config.bundle';
import BASE_CONFIG from '../../config/config.base';
import { useLazyBotTokenQuery } from '../../store/apis/account-api';

const BOT_IFRAME_BASE_URL = integrationsConfig.botIframeUrl;
const BOT_CHAT_DIMENSIONS_KEY = 'bot_chat_dimensions';
const BOT_CHAT_OPEN_KEY = 'bot_chat_is_open';
const BOT_CHAT_MAXIMIZED_KEY = 'bot_chat_is_maximized';
const BOT_CHAT_MIN_WIDTH = 320;
const BOT_CHAT_MIN_HEIGHT = 400;
const BOT_CHAT_DEFAULT_BOTTOM = 120;
const BOT_CHAT_DEFAULT_RIGHT = 30;
const BOT_CHAT_MOUSE_MOVE_EVENT = 'mousemove';
const BOT_CHAT_MOUSE_UP_EVENT = 'mouseup';

type Dimensions = {
  width: number;
  height: number;
  right: number;
  bottom: number;
};

type ResizeHandle = 'left' | 'top' | 'tl' | null;

type DragState = {
  startX: number;
  startY: number;
  startWidth: number;
  startHeight: number;
  startRight: number;
  startBottom: number;
};

const DEFAULT_DIMENSIONS: Dimensions = {
  width: 500,
  height: 600,
  right: BOT_CHAT_DEFAULT_RIGHT,
  bottom: BOT_CHAT_DEFAULT_BOTTOM,
};

export default function BotChatWidget() {
  const user = useAppSelector(state => state.auth.user);
  const isRecLiv = user?.isRecLiv === true;
  const activeProperty = useAppSelector(activePropertySelector);

  const [isOpen, setIsOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const saved = localStorage.getItem(BOT_CHAT_OPEN_KEY);
    return saved === 'true';
  });

  const [isMaximized, setIsMaximized] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    const saved = localStorage.getItem(BOT_CHAT_MAXIMIZED_KEY);
    return saved === 'true';
  });

  const [dimensions, setDimensions] = useState<Dimensions>(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_DIMENSIONS;
    }

    const persisted = sessionStorage.getItem(BOT_CHAT_DIMENSIONS_KEY);
    if (persisted) {
      try {
        const parsed = JSON.parse(persisted) as Dimensions;
        return { ...DEFAULT_DIMENSIONS, ...parsed };
      } catch {
        // ignore parse errors
      }
    }

    return DEFAULT_DIMENSIONS;
  });
  const [viewportHeight, setViewportHeight] = useState<number>(
    typeof window !== 'undefined' ? window.innerHeight : 0
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateHeight = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const [resizeHandle, setResizeHandle] = useState<ResizeHandle>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const lastRequestedContextRef = useRef('');

  const [triggerBotToken, { data: botTokenResult, isLoading, isError }] =
    useLazyBotTokenQuery();

  const botToken = useMemo(() => {
    if (!botTokenResult) return '';
    if (botTokenResult.status && botTokenResult.responseCode === 200) {
      return botTokenResult.responseObject ?? '';
    }
    return '';
  }, [botTokenResult]);

  const iframeSrc = useMemo(() => {
    if (!botToken) return undefined;
    return `${BOT_IFRAME_BASE_URL}?token=${encodeURIComponent(botToken)}`;
  }, [botToken]);

  const getUserIdFromStorage = () => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    try {
      const storageKey = BASE_CONFIG.storageKeys.local.userDetails;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as { sso?: string };
        if (parsed?.sso) {
          return parsed.sso;
        }
      }
    } catch {
      // ignore parse errors
    }

    return undefined;
  };

  const botUserId = useMemo(() => user?.sso || getUserIdFromStorage(), [user]);
  const botAccountNumber = user?.selectedAccountNumber?.trim() || '';
  const isEmployee = user?.typeOfUser === 'employee';
  const hasBotUser = Boolean(botUserId);
  const hasBotAccountNumber = Boolean(botAccountNumber);
  const botLocationId = activeProperty?.LOCATION_ID;
  const botRequestContext = `${botUserId || ''}|${botAccountNumber}|${botLocationId || ''}`;

  useEffect(() => {
    localStorage.setItem(BOT_CHAT_DIMENSIONS_KEY, JSON.stringify(dimensions));
  }, [dimensions]);

  useEffect(() => {
    localStorage.setItem(BOT_CHAT_OPEN_KEY, String(isOpen));
    localStorage.setItem(BOT_CHAT_MAXIMIZED_KEY, String(isMaximized));
  }, [isOpen, isMaximized]);

  useEffect(() => {
    if (isRecLiv) return;
    if (!botUserId) return;
    if (isEmployee && !botAccountNumber) return;
    if (!BOT_IFRAME_BASE_URL) return;
    if (lastRequestedContextRef.current === botRequestContext) return;

    lastRequestedContextRef.current = botRequestContext;
    void triggerBotToken({
      userId: botUserId,
      accountNumber: botAccountNumber,
      locationId: botLocationId,
    });
  }, [
    botAccountNumber,
    botLocationId,
    botRequestContext,
    botUserId,
    isRecLiv,
    isEmployee,
    triggerBotToken,
  ]);

  useEffect(() => {
    if (!resizeHandle || !dragState) return undefined;

    const onPointerMove = (event: MouseEvent) => {
      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;
      const minWidth = BOT_CHAT_MIN_WIDTH;
      const minHeight = BOT_CHAT_MIN_HEIGHT;

      let nextWidth = dragState.startWidth;
      let nextHeight = dragState.startHeight;

      if (resizeHandle === 'left' || resizeHandle === 'tl') {
        nextWidth = Math.max(minWidth, dragState.startWidth - deltaX);
      }
      if (resizeHandle === 'top' || resizeHandle === 'tl') {
        nextHeight = Math.max(minHeight, dragState.startHeight - deltaY);
      }

      setDimensions(prev => ({
        ...prev,
        width: nextWidth,
        height: nextHeight,
      }));
    };

    const onPointerUp = () => {
      setResizeHandle(null);
      setDragState(null);
      document.removeEventListener(BOT_CHAT_MOUSE_MOVE_EVENT, onPointerMove);
      document.removeEventListener(BOT_CHAT_MOUSE_UP_EVENT, onPointerUp);
    };

    document.addEventListener(BOT_CHAT_MOUSE_MOVE_EVENT, onPointerMove);
    document.addEventListener(BOT_CHAT_MOUSE_UP_EVENT, onPointerUp);

    return () => {
      document.removeEventListener('mousemove', onPointerMove);
      document.removeEventListener('mouseup', onPointerUp);
    };
  }, [resizeHandle, dragState]);

  // Guard: render only for authenticated users (Okta or SAML). If employee, account number is mandatory.
  const isEligibleUser = hasBotUser && (!isEmployee || hasBotAccountNumber);
  if (isRecLiv || !isEligibleUser) {
    return null;
  }

  const toggleOpen = () => {
    if (!isOpen) {
      setIsMaximized(false);
    }
    setIsOpen(x => !x);
  };

  const toggleMaximize = () => {
    setIsMaximized(x => !x);
  };

  const startResize =
    (handle: ResizeHandle) => (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();

      setResizeHandle(handle);
      setDragState({
        startX: event.clientX,
        startY: event.clientY,
        startWidth: dimensions.width,
        startHeight: dimensions.height,
        startRight: dimensions.right,
        startBottom: dimensions.bottom,
      });
    };

  const adjustedHeight = isMaximized
    ? viewportHeight
    : Math.min(dimensions.height, Math.max(320, viewportHeight - 120));

  const adjustedBottom = isMaximized
    ? 0
    : Math.min(dimensions.bottom, Math.max(0, viewportHeight - adjustedHeight));

  const toggleIconVisibility = isOpen;

  return (
    <>
      <div
        style={{
          position: 'fixed',
          bottom: 30,
          right: 30,
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <button
          type="button"
          aria-label={isOpen ? 'Close Chat' : 'Open Chat'}
          style={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            border: 'none',
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={toggleOpen}
        >
          {toggleIconVisibility ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                fill="#2563EB"
              />
            </svg>
          ) : (
            <svg
              width="38"
              height="38"
              viewBox="0 0 64 64"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="32" cy="32" r="32" fill="#006BFF" />
              <rect
                x="10"
                y="27"
                width="10"
                height="14"
                rx="4"
                fill="#FFFFFF"
              />
              <rect
                x="44"
                y="27"
                width="10"
                height="14"
                rx="4"
                fill="#FFFFFF"
              />
              <polygon points="26,40 26,54 40,42" fill="#FFFFFF" />
            </svg>
          )}
        </button>
      </div>

      <div
        style={{
          position: 'fixed',
          top: isMaximized ? 0 : 'auto',
          left: isMaximized ? 0 : 'auto',
          bottom: isMaximized ? 0 : adjustedBottom,
          right: isMaximized ? 0 : dimensions.right,
          width: isMaximized ? '100vw' : dimensions.width,
          height: isMaximized ? '100vh' : adjustedHeight,
          maxHeight: isMaximized ? '100vh' : '100vh',
          zIndex: 9999,
          backgroundColor: '#f4f6f8',
          borderRadius: isMaximized ? 0 : 12,
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          display: isOpen ? 'flex' : 'none',
          flexDirection: 'column',
          transition: 'all 0.25s ease',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            position: 'relative',
            padding: '8px',
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <button
            type="button"
            aria-label="Toggle fullscreen"
            style={{
              border: '1px solid rgba(0,0,0,0.1)',
              borderRadius: 6,
              width: 32,
              height: 32,
              backgroundColor: '#fff',
              cursor: 'pointer',
            }}
            onClick={toggleMaximize}
          >
            {isMaximized ? '🗗' : '🗖'}
          </button>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            position: 'relative',
            backgroundColor: '#fafafa',
          }}
        >
          {isLoading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#333',
                fontWeight: 600,
              }}
            >
              Loading chatbot...
            </div>
          )}

          {isError && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#b91c1c',
                fontWeight: 600,
              }}
            >
              Unable to load bot token. Refresh or contact support.
            </div>
          )}

          {iframeSrc ? (
            <iframe
              key={botUserId}
              title="GE Bot Chat"
              src={iframeSrc}
              style={{
                width: '100%',
                height: '100%',
                border: 0,
                borderRadius: isMaximized ? 0 : 12,
              }}
            />
          ) : !BOT_IFRAME_BASE_URL ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#b91c1c',
                fontWeight: 600,
                textAlign: 'center',
                padding: '16px',
              }}
            >
              Bot iframe URL is not configured. Set VITE_BOT_IFRAME_URL in env.
            </div>
          ) : !isLoading && !isError ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6b7280',
                fontStyle: 'italic',
              }}
            >
              Waiting for bot token...
            </div>
          ) : null}

          {!isMaximized && (
            <>
              <div
                onMouseDown={startResize('tl')}
                style={{
                  position: 'absolute',
                  top: -6,
                  left: -6,
                  width: 16,
                  height: 16,
                  cursor: 'nwse-resize',
                  zIndex: 10001,
                }}
              />
              <div
                onMouseDown={startResize('top')}
                style={{
                  position: 'absolute',
                  top: -4,
                  left: 10,
                  right: 0,
                  height: 8,
                  cursor: 'ns-resize',
                  zIndex: 10001,
                }}
              />
              <div
                onMouseDown={startResize('left')}
                style={{
                  position: 'absolute',
                  left: -4,
                  top: 10,
                  bottom: 0,
                  width: 8,
                  cursor: 'ew-resize',
                  zIndex: 10001,
                }}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}
